import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import Button, { ButtonThemes } from "ui-components/button/button";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import searchIcon from "images/search_gray.svg";
import { AddressState } from "slices/address-manager/AddressState";
import { useSelector } from "react-redux";
import { addressSelector } from "slices/address-manager/addressSlice";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import AddressmanagerService from "app/data/address-manager/addressService";
import { AddressNote, noteTypeOptions, noteValidationAddSchema, noteValidationEditSchema } from "app/data/address-manager/models";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import InfiniteScrollableList from "features/address-manager/common/infiniteScrollableList";
import XGSFormTextarea from "ui-components/form/textarea/xgsFormTextarea";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import "./notesWidget.scss";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import Loading from "ui-components/loading/loading";

const NotesWidget = () => {
    const queryClient = useQueryClient();
    const addressState: AddressState = useSelector(addressSelector);
    const [isEdit, setIsEdit] = useState<Number | null>(null);
    const selectedAddressId = addressState?.selectedAddressId;
    const [filter, setFilter] = useState<string>("");
    const [searchString, setSearchString] = useState<string>("");
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [noteId, setNoteId] = useState<string>("");

    const queryKey = [QUERY_KEYS.ADDRESS_NOTES, selectedAddressId, searchString];

    const {
        data: notesData,
        isLoading,
        fetchNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam = 0 }) => {
            const response = await AddressmanagerService.getInstance().getNotesByAddressId(selectedAddressId as string, pageParam, searchString || "");
            return response;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage.last) return undefined;
            return lastPage.pageable.pageNumber + 1;
        },
        keepPreviousData: false,
        refetchOnWindowFocus: false,
        enabled: !!selectedAddressId,
        retry: false,
    });

    const updateNotesMutation = useMutation({
        mutationFn: (data: AddressNote) => {
            return AddressmanagerService.getInstance().updateNotes(data)
        },
        onMutate: async (updatedNote: AddressNote) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot the previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically update to the new value
            queryClient.setQueryData(queryKey, (old: any) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page: any) => ({
                        ...page,
                        content: page.content.map((note: AddressNote) =>
                            note.id === updatedNote.id
                                ? {
                                    ...note,
                                    note: updatedNote.note,
                                    dateUpdated: updatedNote.dateUpdated
                                }
                                : note
                        )
                    }))
                };
            });

            // Return a context with the previous data
            return { previousData };
        },
        onSuccess: () => {
            toast.info("Note updated successfully");
            setIsEdit(null);
        },
        onError: (error, updatedNote, context) => {
            // Rollback to previous data on error
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
            toast.error(ERROR_MESSAGES.COMMON);
        },
        onSettled: () => {
            // Invalidate and refetch to ensure we have the latest data
            queryClient.invalidateQueries({ queryKey });
        }
    });

    const createNotesMutation = useMutation({
        mutationFn: (data: AddressNote) => {
            return AddressmanagerService.getInstance().createNotes(data)
        },
        onSuccess: () => {
            toast.info("Note created successfully");
            refetch();
        },
        onError: () => {
            toast.error(ERROR_MESSAGES.COMMON);
        },
    });

    const deleteNotesMutation = useMutation({
        mutationFn: (noteId: string) => {
            return AddressmanagerService.getInstance().deleteNotes(noteId)
        },
        onMutate: async (deletedNoteId: string) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot the previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically remove the note
            queryClient.setQueryData(queryKey, (old: any) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page: any) => ({
                        ...page,
                        content: page.content.filter((note: AddressNote) => note.id !== deletedNoteId)
                    }))
                };
            });

            return { previousData };
        },
        onSuccess: () => {
            toast.info("Note deleted successfully");
            setShowDeleteModal(false);
            setNoteId("");
        },
        onError: (error, deletedNoteId, context) => {
            // Rollback to previous data on error
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
            toast.error(ERROR_MESSAGES.COMMON);
        },
        onSettled: () => {
            // Always refetch to ensure consistency
            queryClient.invalidateQueries({ queryKey });
        }
    });

    const items = useMemo(() => notesData?.pages.flatMap((page) => page.content) ?? [], [notesData]);
    const itemCount = hasNextPage ? items.length + 1 : items.length;

    const isItemLoaded = useMemo(() => (index: number) => index < items.length, [items]);

    const loadMoreItems = async (startIndex: number, stopIndex: number) => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    const Row = React.useCallback(({ index, style }) => {
        if (!isItemLoaded(index)) {
            return <div style={{ ...style, padding: 10 }}>Loading...</div>;
        }

        const note = items[index];

        return (
            <div className="notes-widget__recent__notes" style={style}>
                <div className="notes-widget__recent__notes__note">
                    <div className="notes-widget__recent__notes__note__title">
                        <div className="notes-widget__recent__notes__note__title__container">
                            <div className="notes-widget__recent__notes__note__title__type">
                                {noteTypeOptions.filter((option) => option.value === note?.noteType)?.[0]?.label ?? note?.noteType}
                            </div>
                            {note?.updatedBy || note?.createdBy}
                        </div>
                        <div className="notes-widget__recent__notes__note__title__icon">
                            <XGSIcon
                                icon={XGSIcons.faTrash}
                                className="notes-widget__recent__notes__note__title__delete"
                                size="sm"
                                title="Delete"
                                onClick={() => {
                                    if (note.id) {
                                        setShowDeleteModal(true);
                                        setNoteId(note.id);
                                    }
                                }}
                            />
                            <XGSIcon
                                icon={XGSIcons.faEdit}
                                className="notes-widget__recent__notes__note__title__edit"
                                size="sm"
                                title="Edit"
                                onClick={() => {
                                    if (isEdit !== index) {
                                        setIsEdit(index);
                                    }
                                    else {
                                        setIsEdit(null);
                                    }
                                }}
                            />
                        </div>
                    </div>
                    {isEdit === index ?
                        <div className="notes-widget__recent__notes__note__content__edit">
                            <Formik
                                initialValues={{ note: note.note }}
                                validationSchema={noteValidationEditSchema()}
                                onSubmit={(values) => {
                                    // Exit edit mode immediately for smooth UX
                                    // setIsEdit(null);

                                    let payload = {
                                        id: note.id,
                                        note: values.note,
                                        noteType: note.noteType,
                                        addressId: note.addressId,
                                    }
                                    updateNotesMutation.mutate(payload as AddressNote);
                                }}
                            >
                                {({ resetForm, dirty }) => <Form>
                                    <XGSFormTextarea
                                        name="note"
                                        required={true}
                                        requiredAsteriskDisabled
                                    />
                                    <div className="notes-widget__recent__notes__note__content__edit__button">
                                        <Button
                                            theme={ButtonThemes.gray}
                                            type="button"
                                            onClick={() => {
                                                setIsEdit(null);
                                                resetForm();
                                            }}
                                            spinner={updateNotesMutation.isLoading}
                                            disabled={updateNotesMutation.isLoading || !dirty}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            theme={ButtonThemes.blue}
                                            type="submit"
                                            spinner={updateNotesMutation.isLoading}
                                            disabled={updateNotesMutation.isLoading || !dirty}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>}
                            </Formik>
                        </div> : <div className="notes-widget__recent__notes__note__content">{note.note}</div>}
                    {!(isEdit === index) && <div className="notes-widget__recent__notes__note__date">{note.dateUpdated?.toUiMonthDateFormat()}</div>}
                </div>
            </div>
        );
    }, [items, isItemLoaded, updateNotesMutation, isEdit]);

    useEffect(() => {
        const handler = setTimeout(() => setSearchString(filter), 1000);
        return () => clearTimeout(handler);
    }, [filter]);

    return (
        <>
            <Formik
                initialValues={{ notes: "", type: "" }}
                onSubmit={(values, { resetForm }) => {
                    let payload = {
                        note: values.notes,
                        noteType: values.type,
                        addressId: selectedAddressId,
                    }
                    createNotesMutation.mutate(payload as AddressNote);
                    // Reset form immediately for better UX
                    resetForm();
                }}
                validationSchema={noteValidationAddSchema()}
            >
                {({ values, setFieldValue, dirty }) => (
                    <Form>
                        <div className="notes-widget">
                            <div className="notes-widget__header">Note Type<XGSFormSelect
                                name="type"
                                options={noteTypeOptions.filter((option) => option.value !== "D")}
                                value={values?.type ? noteTypeOptions.find(
                                    (option) => option.value === values.type
                                ) : null}
                                onValueChange={(option) => {
                                    setFieldValue("type", option?.value ?? "");
                                }}
                                formik
                                className="notes-widget__header__select"
                                labelMode={LabelModes.column}
                            /></div>
                            <div className="notes-widget__content">
                                <div className="notes-widget__content__form">
                                    <XGSFormTextarea
                                        name="notes"
                                        placeholder="Enter your note here..."
                                        required={true}
                                        requiredAsteriskDisabled
                                    />
                                    <div className="notes-widget__content__form__button">
                                        <Button
                                            theme={ButtonThemes.blue}
                                            type="submit"
                                            spinner={createNotesMutation.isLoading}
                                            disabled={!dirty}
                                        >
                                            Add Note
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
            <div className="notes-widget__recent">Recent Notes</div>
            <div className="notes-widget__recent__filter">
                <img src={searchIcon} alt="search" className="notes-widget__recent__filter__searchIcon" />
                <LabeledTextInput
                    name="searchString"
                    labelMode={LabelModes.column}
                    className="xgs-sideaddress-list__input"
                    type="text"
                    value={filter}
                    onKeyDown={(e) => (e.key === "Enter")}
                    onChange={(e) => setFilter(e.currentTarget.value)}
                    placeholder="Search notes..."
                    clearable
                    onClear={() => setFilter("")}
                />
            </div>
            <div style={{ minHeight: "300px" }}>
                {isLoading ? <div className="notes-widget__recent__loading"><Loading isLoading={true} /></div> :
                    items?.length > 0 ? <InfiniteScrollableList
                        isItemLoaded={isItemLoaded}
                        itemCount={itemCount}
                        loadMoreItems={loadMoreItems}
                        children={Row}
                        itemSize={210}
                    /> : <div className="notes-widget__recent__no-results">No notes found {filter.length > 0 ? `for "${filter}"` : ""}</div>}
            </div>
            <ConfirmationModal
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => {
                    if (showDeleteModal) {
                        deleteNotesMutation.mutate(noteId);
                    }
                }}
                opened={!!showDeleteModal}
                spinner={deleteNotesMutation.isLoading}
            >
                <div className="confirmation-modal__content">
                    <AlertIcon />
                    <div>Are you sure you want to delete?</div>
                </div>
            </ConfirmationModal>
        </>
    );
};

export default NotesWidget;